import cx from 'classnames'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef } from 'react'

import { ImageUploadButton } from 'common/Prose/Toolbar/MobileToolbar/buttons/ImageUploadButton/ImageUploadButton'
import { RedoButton } from 'common/Prose/Toolbar/MobileToolbar/buttons/RedoButton'
import { UndoButton } from 'common/Prose/Toolbar/MobileToolbar/buttons/UndoButton'
import { LinkMark } from 'common/Prose/Toolbar/MobileToolbar/components/Marks/Link/LinkMark'
import { useQueryParams } from 'hooks/useQueryParams'
import { useRootStore } from 'stores/RootStore'

import { AlignLeftButton } from './buttons/AlignLeftButton'
import { BulletedListButton } from './buttons/BulletedListButton'
import { CommentButton } from './buttons/CommentButton'
import { TextStyleButton } from './buttons/TextStyleButton'
import { CollaborationDialog } from './CollaborationDialog/CollaborationDialog'
import { BoldMark } from './components/Marks/BoldMark'
import { CodeMark } from './components/Marks/CodeMark'
import { HighlightColor } from './components/Marks/HighlightColor'
import { ItalicMark } from './components/Marks/ItalicMark'
import { LinkModalWrapper } from './components/Marks/Link/LinkModalWrapper'
import { StrikethroughMark } from './components/Marks/StrikethroughMark'
import { TextColor } from './components/Marks/TextColor'
import { UnderlineMark } from './components/Marks/UnderlineMark'
import { useEditorFocusHacks, useMobileToolbarPositionHacks } from './hooks/usePlatformSpecificHacks'
import { ListStyleSelection } from './ListStyleSelection/ListStyleSelection'
import { ScrollHintLeft, ScrollHintRight } from './ScrollHints'
import { useMobileToolbar } from './stores/MobileToolbarStore'
import { TextAlignSelection } from './TextAlignSelection/TextAlignSelection'
import { TextColorSelectionBar, TextColorSelectionDialog } from './TextColorSelection/TextColorSelection'
import {
  TextHighlightSelectionBar,
  TextHighlightSelectionDialog,
} from './TextHighlightSelection/TextHighlightSelection'
import { TextStyleSelection } from './TextStyleSelection/TextStyleSelection'
import { TopTiers } from './Types'

import Styles from './MobileToolbar.module.scss'

export const MobileToolbar = observer(() => {
  const mobileToolbarRef = useRef<HTMLDivElement>(null)
  const collaborationDialogRef = useRef<HTMLDivElement>(null)
  const mobileToolbarStore = useMobileToolbar()
  const rootStore = useRootStore()

  const { hasVirtualKeyboard, isMobileIOS } = useMobileToolbarPositionHacks({
    mobileToolbarRef,
    collaborationDialogRef,
  })
  useEditorFocusHacks()

  const toggleTier = useCallback(
    (targetTier: TopTiers) => () => mobileToolbarStore.toggleTopTier(targetTier),
    [mobileToolbarStore],
  )

  // Scroll hints
  const firstTier = useRef<HTMLDivElement>(null)
  const secondTier = useRef<HTMLDivElement>(null)

  // Currently selected topTier
  const { topTier } = mobileToolbarStore

  // Open the comment dialog immediatelly when coming from a link
  const { id } = useQueryParams()
  const collabItemIdQueryParam = parseInt(id)
  const linkedComment = rootStore.docStore?.comments.find((comment) => comment.id === collabItemIdQueryParam)
  useEffect(() => {
    if (rootStore.docStore && collabItemIdQueryParam) {
      if (linkedComment) {
        rootStore.docStore.rootStore.comments.select(collabItemIdQueryParam)
        if (linkedComment.status === 'resolved') {
          rootStore.docStore.filters.activate('resolved')
        }
        if (mobileToolbarStore.topTier !== TopTiers.Comments) {
          mobileToolbarStore.toggleTopTier(TopTiers.Comments)
        }
      }
    }
  }, [linkedComment, rootStore.docStore, collabItemIdQueryParam, mobileToolbarStore])

  // Open comments when nothing is open and a comment is selected (docStore?.rootStore.comments.select(commentId))
  useEffect(
    () =>
      reaction(
        () => rootStore.comments.selected?.id,
        (selected, prevSelected) => {
          if (!selected || selected === prevSelected || mobileToolbarStore.topTier !== TopTiers.None) return
          const comment = rootStore.docStore?.comments.find((comment) => comment.id === selected)
          if (comment?.status === 'resolved') {
            rootStore.docStore?.filters.activate('resolved')
          } else if (comment) {
            rootStore.docStore?.filters.deactivate('resolved')
          }
          mobileToolbarStore.toggleTopTier(TopTiers.Comments)
        },
      ),
    [rootStore, mobileToolbarStore],
  )

  // Reset scroll of second tier
  useEffect(() => {
    if (!secondTier.current) return
    // iOS Safari scrolls past the content and that breaks the scrollView when switching from H3 to no text color
    secondTier.current.scroll({ left: 0 })
    setTimeout(() => {
      secondTier.current?.querySelector('[class*="active"]')?.scrollIntoView({ behavior: 'smooth', inline: 'center' })
    }, 200)
  }, [secondTier, mobileToolbarStore.topTier])

  return (
    <div
      ref={mobileToolbarRef}
      className={cx(Styles.container, {
        [Styles.virtualKeyboardContainer]: hasVirtualKeyboard,
        [Styles.isMobileIOS]: isMobileIOS,
      })}
      data-testid="mobile-toolbar"
    >
      {topTier === TopTiers.Link && <LinkModalWrapper />}
      {topTier === TopTiers.TextColor && mobileToolbarStore.showDialog && <TextColorSelectionDialog />}
      {topTier === TopTiers.HighlightColor && mobileToolbarStore.showDialog && <TextHighlightSelectionDialog />}
      {topTier === TopTiers.Comments && <CollaborationDialog ref={collaborationDialogRef} />}
      <div
        ref={secondTier}
        className={cx(Styles.secondTier, {
          [Styles.secondTierHidden]: topTier === TopTiers.None || topTier === TopTiers.Comments,
        })}
      >
        <ScrollHintLeft scrollRef={secondTier} topTier={topTier} />
        {topTier === TopTiers.TextStyle && <TextStyleSelection />}
        {topTier === TopTiers.ListStyle && <ListStyleSelection />}
        {topTier === TopTiers.HighlightColor && <TextHighlightSelectionBar />}
        {topTier === TopTiers.TextColor && <TextColorSelectionBar />}
        {topTier === TopTiers.TextAlign && <TextAlignSelection />}
        <ScrollHintRight scrollRef={secondTier} topTier={topTier} />
      </div>
      <div
        ref={firstTier}
        className={cx(Styles.firstTier, {
          [Styles.commentStyles]: topTier === TopTiers.Comments,
        })}
      >
        <ScrollHintLeft scrollRef={firstTier} />
        <div className={cx(Styles.scroll)} onTouchStart={(e) => e.stopPropagation()}>
          <TextStyleButton
            aria-label="Show text styling options"
            selected={topTier === TopTiers.TextStyle}
            onClick={toggleTier(TopTiers.TextStyle)}
          />
          <BulletedListButton
            aria-label="Show list options"
            selected={topTier === TopTiers.ListStyle}
            onClick={toggleTier(TopTiers.ListStyle)}
          />
          <CommentButton aria-label="Show comments" onClick={toggleTier(TopTiers.Comments)} />
          <LinkMark />
          <ImageUploadButton />
          <AlignLeftButton aria-label="Show text alignment options" onClick={toggleTier(TopTiers.TextAlign)} />
          <BoldMark />
          <ItalicMark />
          <UnderlineMark />
          <StrikethroughMark />
          <CodeMark />
          <TextColor onClick={toggleTier(TopTiers.TextColor)} />
          <HighlightColor onClick={toggleTier(TopTiers.HighlightColor)} />
          <UndoButton />
          <RedoButton />
        </div>
        <ScrollHintRight scrollRef={firstTier} />
      </div>
    </div>
  )
})
